/**
 * Configurações de Autenticação e Sessão - Meu Esporte
 * 
 * Este arquivo centraliza todas as configurações relacionadas à autenticação
 * e persistência de sessões do usuário.
 */

// ⏱️ Configurações de Duração de Sessão
export const SESSION_DURATIONS = {
  // Sessão máxima (quando "Manter conectado" está ativado) - Aumentado para próximo do limite do Firebase
  LONG_SESSION: 90 * 24 * 60 * 60 * 1000,      // 90 dias (3 meses)
  
  // Sessão padrão (quando "Manter conectado" está desativado) - Aumentado
  DEFAULT_SESSION: 30 * 24 * 60 * 60 * 1000,   // 30 dias
  
  // Sessão curta (para casos específicos) - Aumentado
  SHORT_SESSION: 7 * 24 * 60 * 60 * 1000,      // 7 dias
  
  // Margem para renovação de token - Aumentado para dar mais tempo
  TOKEN_REFRESH_MARGIN: 30 * 60 * 1000,        // 30 minutos
};

// 🔄 Configurações de Verificação
export const VERIFICATION_CONFIG = {
  // Intervalo para verificação automática de sessão (menos frequente para economizar recursos)
  CHECK_INTERVAL: 30 * 60 * 1000,              // 30 minutos (reduzido de 15)
  
  // Intervalo para verificação em background quando inativo
  BACKGROUND_CHECK_INTERVAL: 60 * 60 * 1000,   // 60 minutos (1 hora)
  
  // Debounce para atividade do usuário
  ACTIVITY_DEBOUNCE: 1000,                     // 1 segundo
  
  // Eventos que indicam atividade do usuário
  ACTIVITY_EVENTS: ['mousedown', 'keydown', 'scroll', 'touchstart'],
  
  // Tempo de inatividade para considerar o usuário como "away" (aumentado)
  INACTIVITY_THRESHOLD: 30 * 60 * 1000,        // 30 minutos
};

// 💾 Configurações de Armazenamento
export const STORAGE_CONFIG = {
  // Chaves para localStorage/sessionStorage
  USER_KEY: 'user',
  REFRESH_TOKEN_KEY: '@MeuEsporte:refreshToken',
  REMEMBER_EMAIL_KEY: 'meuEsporte_rememberEmail',
  REDIRECT_DATA_KEY: 'redirectData',
  
  // Tipo de persistência padrão
  DEFAULT_PERSISTENCE: 'local', // 'local' ou 'session'
};

// 🎨 Configurações de Interface
export const UI_CONFIG = {
  // Textos para diferentes durações
  DURATION_LABELS: {
    [SESSION_DURATIONS.LONG_SESSION]: '30 dias',
    [SESSION_DURATIONS.DEFAULT_SESSION]: '7 dias',
    [SESSION_DURATIONS.SHORT_SESSION]: '24 horas',
  },
  
  // Descrições para o toggle "Manter Conectado"
  REMEMBER_ME_DESCRIPTIONS: {
    enabled: 'Você permanecerá logado por até 30 dias, mesmo fechando o navegador',
    disabled: 'Você permanecerá logado por 7 dias ou até fechar o navegador',
  },
  
  // Configuração padrão do toggle
  DEFAULT_REMEMBER_ME: true,
  
  // Configurações de feedback visual
  VISUAL_FEEDBACK: {
    SHOW_SESSION_RENEWAL: false,        // Não mostrar toast de renovação automática
    SHOW_INACTIVITY_WARNING: true,      // Avisar quando sessão está próxima de expirar
    WARNING_THRESHOLD: 5 * 60 * 1000,   // Avisar 5 minutos antes de expirar
  },
};

// 🔐 Configurações de Segurança
export const SECURITY_CONFIG = {
  // Número máximo de tentativas de login
  MAX_LOGIN_ATTEMPTS: 5,
  
  // Tempo de bloqueio após muitas tentativas (em ms)
  LOGIN_LOCKOUT_DURATION: 15 * 60 * 1000,      // 15 minutos
  
  // Força verificação de token em determinadas ações
  FORCE_TOKEN_VERIFICATION: [
    '/admin',
    '/configuracoes',
    '/perfil/editar',
  ],
};

// 📱 Configurações específicas para PWA
export const PWA_CONFIG = {
  // Configurações de persistência em dispositivos móveis
  MOBILE_SESSION_DURATION: SESSION_DURATIONS.LONG_SESSION,
  
  // Verificação menos frequente em dispositivos móveis para economizar bateria
  MOBILE_CHECK_INTERVAL: 10 * 60 * 1000,       // 10 minutos
};

// 🛠️ Funções utilitárias para configuração
export const CONFIG_UTILS = {
  /**
   * Obtém a duração da sessão baseada na preferência do usuário
   */
  getSessionDuration: (rememberMe = false) => {
    return rememberMe ? SESSION_DURATIONS.LONG_SESSION : SESSION_DURATIONS.DEFAULT_SESSION;
  },
  
  /**
   * Obtém o label da duração para exibição
   */
  getDurationLabel: (rememberMe = false) => {
    const duration = CONFIG_UTILS.getSessionDuration(rememberMe);
    return UI_CONFIG.DURATION_LABELS[duration] || 'Personalizado';
  },
  
  /**
   * Verifica se é um dispositivo móvel
   */
  isMobileDevice: () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },
  
  /**
   * Obtém o intervalo de verificação apropriado
   */
  getCheckInterval: () => {
    return CONFIG_UTILS.isMobileDevice() 
      ? PWA_CONFIG.MOBILE_CHECK_INTERVAL 
      : VERIFICATION_CONFIG.CHECK_INTERVAL;
  },
};

// 📊 Configurações de Analytics (para futuras melhorias)
export const ANALYTICS_CONFIG = {
  // Eventos para tracking
  EVENTS: {
    LOGIN_SUCCESS: 'auth_login_success',
    LOGIN_FAILURE: 'auth_login_failure',
    SESSION_EXTENDED: 'auth_session_extended',
    SESSION_EXPIRED: 'auth_session_expired',
    REMEMBER_ME_TOGGLED: 'auth_remember_me_toggled',
  },
  
  // Métricas para acompanhamento
  METRICS: {
    SESSION_DURATION: 'auth_session_duration',
    LOGIN_FREQUENCY: 'auth_login_frequency',
    REMEMBER_ME_USAGE: 'auth_remember_me_usage',
  },
};

export default {
  SESSION_DURATIONS,
  VERIFICATION_CONFIG,
  STORAGE_CONFIG,
  UI_CONFIG,
  SECURITY_CONFIG,
  PWA_CONFIG,
  CONFIG_UTILS,
  ANALYTICS_CONFIG,
};
